import { DOCUMENT, Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, filter, merge, of, switchMap } from 'rxjs';
import { JsonLdContext, PageSeo, SEO_CONFIG } from './seo.models';

const JSON_LD_MARKER = 'data-seo-json-ld';

/**
 * Applies per-route SEO data (route `data.seo`, see {@link PageSeo}):
 * localized title/description, Open Graph and Twitter tags, canonical and
 * hreflang links, and JSON-LD structured data. Works during SSR and in the
 * browser; re-applies tags on navigation and on language switch.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  readonly #router = inject(Router);
  readonly #title = inject(Title);
  readonly #meta = inject(Meta);
  readonly #translate = inject(TranslateService);
  readonly #document = inject(DOCUMENT);
  readonly #config = inject(SEO_CONFIG);

  #subscription: Subscription | null = null;

  init(): void {
    if (this.#subscription) return;

    this.#subscription = merge(
      this.#router.events.pipe(filter(event => event instanceof NavigationEnd)),
      this.#translate.onLangChange
    )
      .pipe(
        switchMap(() => {
          const { seo } = this.#resolveRoute();
          if (!seo) return of(null);
          const keys = [seo.titleKey, seo.descriptionKey, seo.keywordsKey].filter((key): key is string => !!key);
          // get() waits until the translation file is loaded
          return this.#translate.get(keys);
        })
      )
      .subscribe(translations => {
        if (translations) this.#apply();
      });
  }

  #resolveRoute(): { seo: PageSeo | null; lang: string } {
    let snapshot: ActivatedRouteSnapshot | null = this.#router.routerState.snapshot.root;
    let seo: PageSeo | null = null;
    let lang = this.#config.defaultLang;

    while (snapshot) {
      if (snapshot.data['seo']) seo = snapshot.data['seo'] as PageSeo;
      if (snapshot.params['lang']) lang = snapshot.params['lang'] as string;
      snapshot = snapshot.firstChild;
    }

    return { seo, lang };
  }

  #apply(): void {
    const { seo, lang } = this.#resolveRoute();
    if (!seo) return;

    const t = (key: string): string => this.#translate.instant(key) as string;
    const url = this.#pageUrl(seo.path, lang);
    const title = t(seo.titleKey);
    const description = t(seo.descriptionKey);
    const image = this.#absoluteUrl(this.#config.defaultImage);

    this.#title.setTitle(title);

    this.#setMeta('description', description);
    if (seo.keywordsKey) this.#setMeta('keywords', t(seo.keywordsKey));

    this.#setMeta('og:title', title, 'property');
    this.#setMeta('og:description', description, 'property');
    this.#setMeta('og:url', url, 'property');
    this.#setMeta('og:type', 'website', 'property');
    this.#setMeta('og:site_name', this.#config.siteName, 'property');
    this.#setMeta('og:image', image, 'property');
    this.#setMeta('og:locale', lang, 'property');

    this.#setMeta('twitter:card', 'summary_large_image');
    this.#setMeta('twitter:title', title);
    this.#setMeta('twitter:description', description);
    this.#setMeta('twitter:image', image);

    this.#setCanonicalAndAlternates(seo.path, lang);
    this.#setJsonLd(seo, url, lang, t);
    this.#document.documentElement.lang = lang;
  }

  #setMeta(name: string, content: string, attr: 'name' | 'property' = 'name'): void {
    this.#meta.updateTag({ [attr]: name, content });
  }

  /**
   * The default language is served at the unprefixed URL, so its prefixed
   * variant canonicalizes to the unprefixed one to avoid duplicate content.
   */
  #pageUrl(path: string, lang: string): string {
    const suffix = path ? `/${path}` : '';
    if (lang === this.#config.defaultLang) {
      return `${this.#config.baseUrl}${suffix || '/'}`;
    }
    return `${this.#config.baseUrl}/${lang}${suffix}`;
  }

  #absoluteUrl(url: string): string {
    return url.startsWith('http') ? url : `${this.#config.baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  #setCanonicalAndAlternates(path: string, lang: string): void {
    const head = this.#document.head;

    head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach(link => link.remove());

    const appendLink = (rel: string, href: string, hreflang?: string): void => {
      const link = this.#document.createElement('link');
      link.setAttribute('rel', rel);
      if (hreflang) link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', href);
      head.appendChild(link);
    };

    appendLink('canonical', this.#pageUrl(path, lang));
    for (const supportedLang of this.#config.supportedLangs) {
      appendLink('alternate', this.#pageUrl(path, supportedLang), supportedLang);
    }
    appendLink('alternate', this.#pageUrl(path, this.#config.defaultLang), 'x-default');
  }

  #setJsonLd(seo: PageSeo, url: string, lang: string, t: (key: string) => string): void {
    this.#document.querySelectorAll(`script[${JSON_LD_MARKER}]`).forEach(script => script.remove());

    if (!seo.jsonLd?.length) return;

    const context: JsonLdContext = { url, baseUrl: this.#config.baseUrl, lang, config: this.#config, t };

    for (const build of seo.jsonLd) {
      const script = this.#document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(JSON_LD_MARKER, '');
      script.text = JSON.stringify({ '@context': 'https://schema.org', ...build(context) });
      this.#document.head.appendChild(script);
    }
  }
}
